import { formatNumber } from "@core/helpers"
import {
    isOptimTurpeTemplateC5 as isTemplateC5,
    isOptimTurpeTemplateC4 as isTemplateC4,
    isOptimTurpeTemplateC2 as isTemplateC2,
} from "@modules/courtage/constants/optim-turpe"
import type { OptimTurpeMetric } from "@modules/courtage/types/optim-turpe"

type Data = Record<string, OptimTurpeMetric | undefined>

function numValue(m: OptimTurpeMetric | undefined): number | undefined {
    const v = m?.value
    return typeof v === "number" && !Number.isNaN(v) ? v : undefined
}

function displayMetric(
    m: OptimTurpeMetric | undefined,
    fallback = "-"
): string {
    if (!m) return fallback
    if (m.str_value != null && String(m.str_value).trim() !== "") {
        return String(m.str_value)
    }
    if (m.value == null || m.value === "") {
        return fallback
    }
    return String(m.value)
}

/** Champ métier `optimisation_fta` : le legacy lit uniquement `.value`, pas `str_value`. */
function optimisationFtaValueRaw(m: OptimTurpeMetric | undefined): string {
    if (!m) return ""
    const v = m.value
    if (v === null || v === undefined || v === "") return ""
    return String(v).trim()
}

/** Affichage puissance souscrite C5 : priorité à `str_value` (souvent « 12 kVA »), puis valeur + unité. */
function formatPuissanceSouscriteKva(m: OptimTurpeMetric | undefined): string {
    if (!m) return "-"
    if (m.str_value != null && String(m.str_value).trim() !== "") {
        return String(m.str_value).trim()
    }
    if (typeof m.value === "number" || typeof m.value === "string") {
        return `${m.value} ${m.unit ?? "kVA"}`.trim()
    }
    return "-"
}

function formatEuro(value: number | null | undefined, fallback = "-"): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return fallback
    }
    return formatNumber(value, { style: "currency" })
}

/**
 * Couleur d'une économie : vert (success) si gain (> 0), rouge (danger) si la
 * composante coûte plus cher après optimisation (< 0, le client perd sur ce
 * poste), neutre si zéro ou pas de valeur. Symétrique du test de signe déjà
 * appliqué à l'évolution CMDPS (un montant négatif y vaut un gain car c'est un
 * coût).
 */
function ecoVariant(
    value: number | undefined
): "success" | "danger" | undefined {
    if (value === undefined || value === 0) return undefined
    return value > 0 ? "success" : "danger"
}

/**
 * Libellés dépendant du SIGNE d'un montant (DEV-1621).
 *
 * Le fait : sur le compteur 50013169271008 (rapport du 24/06/2026), la part fixe
 * vaut `cs_fixe_avant 2361,91` contre `cs_fixe_apres 3089,02`. La différence
 * `avant − après` fait **−727,12 €**, et l'écran l'affichait sous l'intitulé
 * « Économies possibles ». L'arithmétique était juste (la part variable, elle,
 * gagne 1 496,20 €, total 660,51 €) mais la phrase promettait une économie là où
 * le poste coûte plus cher.
 *
 * Un montant ne porte donc plus son signe dans un intitulé qui promet le
 * contraire : un résultat négatif se NOMME « surcoût » et s'affiche en valeur
 * absolue, un résultat nul se nomme « aucune économie identifiée ».
 */
export type NommageSigne = {
    /** Intitulé quand le poste rapporte (montant strictement positif). */
    titreEconomie: string
    /** Intitulé quand le poste coûte (montant strictement négatif). */
    titreSurcout: string
    /** Intitulé quand le poste ne change rien (montant nul). */
    titreAucuneEconomie: string
}

/**
 * Nomme un gain selon son signe et renvoie le montant à afficher (toujours
 * positif : le mot porte le sens, pas le signe).
 * @param gain - Montant favorable au client (positif = économie).
 * @param nommage - Les trois intitulés selon le signe.
 * @returns Intitulé, montant formaté et variante de couleur.
 */
function nommerGain(
    gain: number,
    nommage: NommageSigne
): {
    label: string
    displayValue: string
    variant: SoutirageKpiValue["variant"]
} {
    if (gain < 0) {
        return {
            label: nommage.titreSurcout,
            displayValue: formatEuro(-gain),
            variant: "danger",
        }
    }
    if (gain === 0) {
        return {
            label: nommage.titreAucuneEconomie,
            displayValue: formatEuro(0),
            variant: undefined,
        }
    }
    return {
        label: nommage.titreEconomie,
        displayValue: formatEuro(gain),
        variant: "success",
    }
}

/** Ordre d’affichage — aligné sur `getActualRangePower` (homeys-front). */
const PLAGE_ORDER_DISPLAY = ["hph", "hch", "hpb", "hcb"] as const
type PlageKey = (typeof PLAGE_ORDER_DISPLAY)[number] | "pte"

function plageKeysForSegment(segment: string): PlageKey[] {
    const base: PlageKey[] = [...PLAGE_ORDER_DISPLAY]
    if (isTemplateC2(segment)) {
        return ["pte", ...base]
    }
    return base
}

export type SoutirageKpiValue = {
    label: string
    displayValue: string
    unit?: string
    sub?: string
    variant?: "success" | "primary" | "danger"
    /** Texte « pas de valeur » métier (ex. FTA non optimisable). */
    noValueText?: string
    /** Tooltip du libellé (ex. économies fixes C5). */
    labelPopup?: string
    /** Tooltip sur la zone valeur (ex. explication FTA partielle). */
    labelSecondaryPopup?: string
    /** Tooltip valeur quand Pas de montant mais besoin d’aide infra-horaire. */
    valuePopup?: string
    /** Indique un placeholder à résoudre côté UI (`ecoFix` | `ecoVar`). */
    emptyReason?: "ecoFix" | "ecoVar"
}

export type SoutirageRangePlage = {
    plageKey: string
    label: string
    labelHelp: string
    displayValue: string
}

/**
 * Présentation des blocs « partie fixe / variable / CMDPS » (équivalent
 * `getFixPartKpiC5`, `getEconomyAvailable`, `getKpiVarPart`, `getCMDPSValues`
 * + dénivelés dans `OptimTurpeResultContent.vue` homeys-front).
 */
export function buildOptimSoutiragePresentation(
    raw: Data | undefined,
    segment: string,
    messages: {
        subtitleActual: string
        subtitleOptimal: string
        ecoC4SingleLabel: string
        rangeSeason: Record<string, string>
        rangeHelp: Record<string, string>
        defaultPuissanceTitle: string
        defaultPuissanceOptimaleTitle: string
        defaultEcoTitle: string
        defaultEcoVarTitle: string
        ftaActuelleTitle: string
        ftaOptimaleTitleBase: string
        ftaOptimaleBlocked: string
        ftaPartialExplainer: string
        avantOptimTitle: string
        apresOptimTitle: string
        evolutionCmdpsTitle: string
        /** Intitulé d'un montant NÉGATIF (le poste coûte plus cher). DEV-1621. */
        surcoutTitle: string
        /** Intitulé d'un montant NUL (rien à gagner sur ce poste). DEV-1621. */
        aucuneEconomieTitle: string
        /** Intitulé d'un CMDPS en HAUSSE : une pénalité nouvelle, pas une économie. */
        cmdpsSurcoutTitle: string
        /** Intitulé d'un CMDPS en BAISSE : une pénalité évitée. */
        cmdpsEconomieTitle: string
        noDeniveleDataFta: string
        noDeniveleOptimal: string
        ecoFixPopup: string
        ecoVarPopup: string
        ecoC4Popup: string
        cmdpsEvolutionPopup: string
        /** Explication pas de données infra-horaires (FTA / éco. variable). */
        infraHourlyHistoricalHint: string
    }
): {
    segment: string
    showDeniveleBlocks: boolean
    deniveleActual: {
        subtitle: string
        plages: SoutirageRangePlage[]
        isEmpty: boolean
        noDataBanner: string
    }
    deniveleOptimal: {
        subtitle: string
        plages: SoutirageRangePlage[]
        isEmpty: boolean
        noDataBanner: string
    }
    fixBlock: {
        popupIsC5: boolean
        rows: SoutirageKpiValue[]
    }
    varBlock: {
        rows: SoutirageKpiValue[]
    }
    cmdpsBlock: {
        rows: SoutirageKpiValue[]
    } | null
} {
    const data = raw ?? {}
    const segmentValue = segment || ""

    const keys = plageKeysForSegment(segmentValue)
    const actualPlages: SoutirageRangePlage[] = []
    const optimalPlages: SoutirageRangePlage[] = []

    const unitPuissance = isTemplateC2(segmentValue) ? "kW" : "kVA"

    for (const tc of keys) {
        const upper = tc.toUpperCase()
        const avant = data[`P_${upper}_avant`]
        const apres = data[`P_${upper}_apres`]
        const saison = messages.rangeSeason[tc] ?? ""

        const fmtPuissance = (m: OptimTurpeMetric | undefined): string => {
            if (!m) return `- ${unitPuissance}`
            if (m.str_value != null && String(m.str_value).trim() !== "") {
                return `${m.str_value} ${m.unit ?? unitPuissance}`
            }
            if (typeof m.value === "number") {
                return `${m.value} ${m.unit ?? unitPuissance}`
            }
            return `- ${unitPuissance}`
        }

        actualPlages.push({
            plageKey: tc,
            label: `${upper}${saison}`,
            labelHelp: messages.rangeHelp[tc] ?? "",
            displayValue: fmtPuissance(avant),
        })

        optimalPlages.push({
            plageKey: tc,
            label: `${upper}${saison}`,
            labelHelp: messages.rangeHelp[tc] ?? "",
            displayValue: fmtPuissance(apres),
        })
    }

    const hasUsablePuissance = (m: OptimTurpeMetric | undefined): boolean => {
        if (!m) return false
        if (m.str_value != null && String(m.str_value).trim() !== "") {
            return true
        }
        return typeof m.value === "number"
    }
    const actualEmpty = keys.every((tc) => {
        const m = data[`P_${tc.toUpperCase()}_avant`]
        return !hasUsablePuissance(m)
    })
    const optimalEmpty = keys.every((tc) => {
        const m = data[`P_${tc.toUpperCase()}_apres`]
        return !hasUsablePuissance(m)
    })

    const showDeniveleBlocks =
        isTemplateC4(segmentValue) || isTemplateC2(segmentValue)

    // ---------- FTA titles (legacy template mapping) ----------
    // Aligné `optimisation-contractuelle.vue` L379–383 : le message bloquant **uniquement**
    // si `value === 'Non optimisable'` (casse exacte). Une valeur `non optimisable` en
    // minuscules tombe dans le `else if` → sous-titre « FTA … » + affichage `fta_apres`,
    // pas le libellé « impossible ».
    const optimisationFtaRaw = optimisationFtaValueRaw(data.optimisation_fta)
    let ftaOptimaleSubtitle = messages.ftaOptimaleTitleBase
    let ftaOptimaleNoValue: string | undefined
    const optimisationFtaValue = data.optimisation_fta?.value
    if (optimisationFtaValue === "Non optimisable") {
        ftaOptimaleNoValue = messages.ftaOptimaleBlocked
    } else if (optimisationFtaRaw) {
        ftaOptimaleSubtitle = `FTA ${optimisationFtaRaw.toLowerCase()}`
    }

    // str_value prioritaire pour les codes FTA (ex. BTINFMU4) — voir displayMetric.
    const ftaActuelleDisplay = displayMetric(data.fta_avant)
    const ftaOptimaleDisplay = displayMetric(data.fta_apres)

    const fixRows: SoutirageKpiValue[] = []
    let fixPopupIsC5 = false

    if (isTemplateC5(segmentValue)) {
        fixPopupIsC5 = true
        const pa = data.puissance_souscrite_avant
        const po = data.puissance_souscrite_apres
        const puissanceActuelle = formatPuissanceSouscriteKva(pa)
        const puissanceOptimale = formatPuissanceSouscriteKva(po)

        const ecoFixRaw = numValue(data.economies_positives_cs_fixe_avec_cta)
        const ecoFixNomme =
            ecoFixRaw !== undefined
                ? nommerGain(ecoFixRaw, {
                      titreEconomie: messages.defaultEcoTitle,
                      titreSurcout: messages.surcoutTitle,
                      titreAucuneEconomie: messages.aucuneEconomieTitle,
                  })
                : undefined

        fixRows.push(
            {
                label: messages.defaultPuissanceTitle,
                displayValue: puissanceActuelle,
            },
            {
                label: messages.defaultPuissanceOptimaleTitle,
                displayValue: puissanceOptimale,
            },
            {
                label: ecoFixNomme?.label ?? messages.defaultEcoTitle,
                displayValue: ecoFixNomme?.displayValue ?? "",
                unit: "an",
                variant: ecoVariant(ecoFixRaw),
                sub: "HTVA",
                emptyReason:
                    ecoFixNomme === undefined ? ("ecoFix" as const) : undefined,
                labelPopup: messages.ecoFixPopup,
            }
        )
    } else if (isTemplateC4(segmentValue) || isTemplateC2(segmentValue)) {
        const avant = numValue(data.cs_fixe_avant)
        const apres = numValue(data.cs_fixe_apres)
        const diff =
            avant !== undefined && apres !== undefined
                ? avant - apres
                : undefined
        const nomme =
            diff !== undefined
                ? nommerGain(diff, {
                      titreEconomie: messages.ecoC4SingleLabel,
                      titreSurcout: messages.surcoutTitle,
                      titreAucuneEconomie: messages.aucuneEconomieTitle,
                  })
                : undefined

        fixRows.push({
            label: nomme?.label ?? messages.ecoC4SingleLabel,
            displayValue: nomme?.displayValue ?? "",
            unit: "an",
            variant: ecoVariant(diff),
            sub: "HTVA",
            emptyReason: nomme === undefined ? "ecoFix" : undefined,
            labelPopup: messages.ecoC4Popup,
        })
    }

    const csVarAvant = numValue(data.cs_variable_avant)
    const csVarApres = numValue(data.cs_variable_apres)
    const ecoVarDiff =
        csVarAvant !== undefined && csVarApres !== undefined
            ? csVarAvant - csVarApres
            : undefined
    const ecoVarNomme =
        ecoVarDiff !== undefined
            ? nommerGain(ecoVarDiff, {
                  titreEconomie: messages.defaultEcoVarTitle,
                  titreSurcout: messages.surcoutTitle,
                  titreAucuneEconomie: messages.aucuneEconomieTitle,
              })
            : undefined

    const economiesVarMetric = data.economies_possibles_var
    const economiesVarNoValueFlag = Boolean(economiesVarMetric?.noValue)

    const ftaOptimPartial =
        ftaOptimaleSubtitle === "FTA partiellement optimisée"

    const varRows: SoutirageKpiValue[] = [
        {
            label: messages.ftaActuelleTitle,
            displayValue: ftaActuelleDisplay,
        },
        {
            label: ftaOptimaleSubtitle,
            displayValue: ftaOptimaleNoValue ? "" : ftaOptimaleDisplay,
            noValueText: ftaOptimaleNoValue,
            labelSecondaryPopup: ftaOptimPartial
                ? messages.ftaPartialExplainer
                : undefined,
            valuePopup:
                data.fta_apres?.noValue === true ||
                String(data.fta_apres?.noValue) === "true"
                    ? messages.infraHourlyHistoricalHint
                    : undefined,
        },
        {
            label: ecoVarNomme?.label ?? messages.defaultEcoVarTitle,
            displayValue: ecoVarNomme?.displayValue ?? "",
            unit: "an",
            variant: ecoVariant(ecoVarDiff),
            sub: "HTVA",
            emptyReason:
                ecoVarNomme === undefined || economiesVarNoValueFlag
                    ? "ecoVar"
                    : undefined,
            labelPopup: messages.ecoVarPopup,
            valuePopup:
                economiesVarNoValueFlag || ecoVarNomme === undefined
                    ? messages.infraHourlyHistoricalHint
                    : undefined,
        },
    ]

    let cmdpsBlock: {
        rows: SoutirageKpiValue[]
    } | null = null

    if (isTemplateC4(segmentValue) || isTemplateC2(segmentValue)) {
        const diffCmdps =
            numValue(data.cmdps_apres) !== undefined &&
            numValue(data.cmdps_avant) !== undefined
                ? (numValue(data.cmdps_apres) ?? 0) -
                  (numValue(data.cmdps_avant) ?? 0)
                : undefined

        cmdpsBlock = {
            rows: [
                {
                    label: messages.avantOptimTitle,
                    displayValue: formatEuro(numValue(data.cmdps_avant)),
                },
                {
                    label: messages.apresOptimTitle,
                    displayValue: formatEuro(numValue(data.cmdps_apres)),
                },
                // Le CMDPS est un COÛT : `diffCmdps > 0` est une pénalité
                // nouvelle. L'écran l'affichait sous « Évolution +108,58 € »,
                // dans une page qui promet des économies (DEV-1621). On nomme
                // donc le sens, et le montant reste positif.
                diffCmdps === undefined
                    ? {
                          label: messages.evolutionCmdpsTitle,
                          displayValue: "-",
                          unit: "an",
                          variant: undefined,
                          sub: "HTVA",
                          labelPopup: messages.cmdpsEvolutionPopup,
                      }
                    : {
                          ...nommerGain(-diffCmdps, {
                              titreEconomie: messages.cmdpsEconomieTitle,
                              titreSurcout: messages.cmdpsSurcoutTitle,
                              titreAucuneEconomie: messages.evolutionCmdpsTitle,
                          }),
                          unit: "an",
                          sub: "HTVA",
                          labelPopup: messages.cmdpsEvolutionPopup,
                      },
            ],
        }
    }

    return {
        segment: segmentValue,
        showDeniveleBlocks,
        deniveleActual: {
            subtitle: messages.subtitleActual,
            plages: actualPlages,
            isEmpty: actualEmpty,
            noDataBanner: messages.noDeniveleDataFta,
        },
        deniveleOptimal: {
            subtitle: messages.subtitleOptimal,
            plages: optimalPlages,
            isEmpty: optimalEmpty,
            noDataBanner: messages.noDeniveleOptimal,
        },
        fixBlock: {
            popupIsC5: fixPopupIsC5,
            rows: fixRows,
        },
        varBlock: {
            rows: varRows,
        },
        cmdpsBlock,
    }
}
